import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { consumeCredit, refundCredit, isAdminUser, InsufficientCreditsError } from "./_credits";
import { withOscola } from "@/lib/oscola-policy";
import type { SupabaseClient } from "@supabase/supabase-js";

async function loadCitationSetOverlay(
  supabase: SupabaseClient,
  userId: string,
  citationSetId?: string | null,
): Promise<string> {
  if (!citationSetId) return "";
  const { data } = await supabase
    .from("citation_sets")
    .select("name,style,rules,bibliography")
    .eq("id", citationSetId)
    .eq("user_id", userId)
    .maybeSingle();
  if (!data) return "";
  const parts: string[] = [];
  parts.push(`\n\nSAVED CITATION SET — "${data.name}" (${data.style}). Apply EVERY rule below in addition to OSCOLA. If a rule below conflicts with OSCOLA, OSCOLA still wins for shape but the saved rule refines wording/order/pinpoint preferences.`);
  if (data.rules && String(data.rules).trim()) {
    parts.push(`Saved rules:\n${String(data.rules).trim()}`);
  }
  if (data.bibliography && String(data.bibliography).trim()) {
    parts.push(
      `Preferred bibliography entries (reuse verbatim when the source is cited; merge into the final Bibliography and Tables of Authorities alphabetically, deduplicated):\n${String(data.bibliography).trim()}`,
    );
  }
  parts.push(
    `Consistency requirement: the final Citation Check section MUST also verify that every entry in the saved bibliography that was cited in the body appears in the Bibliography/Tables exactly as saved (or note the deviation with a corrected form).`,
  );
  return parts.join("\n\n");
}


const DISCLAIMER =
  "AI-generated using OSCOLA. Verify every authority, citation, pinpoint and legal proposition against the primary source (SAFLII, LawLibrary.org.za, gov.za or the official law report) before relying on it in academic, professional or court work.";

async function callGateway(messages: { role: string; content: string }[], temperature = 0.25) {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) throw new Error("AI not configured");
  const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
    body: JSON.stringify({
      model: "google/gemini-3-flash-preview",
      messages,
      temperature,
    }),
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("RATE_LIMIT");
    if (res.status === 402) throw new Error("CREDITS_EXHAUSTED");
    throw new Error("AI_UNAVAILABLE");
  }
  const data = (await res.json()) as { choices?: { message?: { content?: string } }[] };
  return data.choices?.[0]?.message?.content?.trim() ?? "";
}

async function withCredit<T>(
  userId: string,
  reason: string,
  fn: (refundOnFalsy: () => Promise<void>) => Promise<T>,
): Promise<{ result: T; balance: number }> {
  const ref = `${reason}:${crypto.randomUUID()}`;
  const bypass = await isAdminUser(userId);
  let balance: number;
  if (bypass) {
    balance = Number.POSITIVE_INFINITY;
  } else {
    try {
      balance = await consumeCredit(userId, reason, ref);
    } catch (e) {
      if (e instanceof InsufficientCreditsError) throw new Error("INSUFFICIENT_CREDITS");
      throw e;
    }
  }
  let refunded = false;
  const refund = async () => {
    if (refunded) return;
    refunded = true;
    if (!bypass) await refundCredit(userId, `${reason}_refund`, ref);
  };
  try {
    const result = await fn(refund);
    return { result, balance: refunded && !bypass ? balance + 1 : balance };
  } catch (err) {
    await refund();
    throw err;
  }
}

// ---------------- IRAC generator ----------------

const iracInput = z.object({
  facts: z.string().trim().min(40, "Give us at least a couple of sentences of facts.").max(6000),
  question: z.string().trim().min(8).max(600),
  jurisdiction: z.string().trim().max(120).optional().default("South Africa"),
  citationSetId: z.string().uuid().optional(),
});

export const generateIRAC = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => iracInput.parse(i))
  .handler(async ({ data, context }) => {
    const { result, balance } = await withCredit(context.userId, "ai_irac", async (refund) => {
      const overlay = await loadCitationSetOverlay(context.supabase, context.userId, data.citationSetId);
      const system = withOscola(
        `You are a senior South African legal-research assistant producing publication-grade IRAC analysis for LLB students, candidate attorneys and junior practitioners. ` +
        `Apply the IRAC method (Issue, Rule, Application, Conclusion) with the rigour expected of a Record Of Law research article.\n` +
        `Rules:\n` +
        `1. Output four labelled sections in this exact order and headings: "Issue", "Rule", "Application", "Conclusion".\n` +
        `2. Each section is a tight, well-reasoned paragraph (not bullets). The Rule section must anchor to primary authority (Constitution, statute, binding case) before secondary sources.\n` +
        `3. Every legal proposition MUST carry an OSCOLA footnote marker in the form [^1], [^2], numbered sequentially. List the full OSCOLA footnotes under a final "Footnotes" heading.\n` +
        `4. If uncertain about any citation element, mark it [REQUIRES VERIFICATION — what to check]. NEVER fabricate.\n` +
        `5. Stay within the stated jurisdiction; where facts span multiple, name the applicable jurisdiction in the Rule.\n` +
        `6. Conclusion: a single decisive answer to the legal question — no hedging or disclaimers (the platform adds its own).` + overlay,
      );
      const user =
        `Jurisdiction: ${data.jurisdiction}\nLegal question: ${data.question}\n\nFacts:\n${data.facts}`;
      const out = await callGateway(
        [{ role: "system", content: system }, { role: "user", content: user }],
        0.2,
      );
      if (!out) {
        await refund();
        return { ok: false as const, error: "Could not generate an IRAC analysis. Try clearer facts." };
      }
      return { ok: true as const, output: out, disclaimer: DISCLAIMER };
    });
    return { ...result, balance };
  });

// ---------------- Case analysis ----------------

const caseInput = z.object({
  caseName: z.string().trim().min(3).max(240),
  citation: z.string().trim().max(240).optional().default(""),
  facts: z.string().trim().max(8000).optional().default(""),
  notes: z.string().trim().max(2000).optional().default(""),
  citationSetId: z.string().uuid().optional(),
});

export const analyseCase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => caseInput.parse(i))
  .handler(async ({ data, context }) => {
    const { result, balance } = await withCredit(context.userId, "ai_case_analysis", async (refund) => {
      const overlay = await loadCitationSetOverlay(context.supabase, context.userId, data.citationSetId);
      const system = withOscola(
        `You are a senior case-brief author producing publication-grade briefs to Record Of Law standards. Produce a structured case brief with these headings in this exact order: ` +
        `"Citation" (full OSCOLA neutral + report citation), "Parties", "Procedural history", "Material facts", "Legal issue(s)", "Holding / Ratio decidendi", "Reasoning" (with pinpoints), "Order", "Significance".\n` +
        `Rules:\n` +
        `1. Use ONLY facts the user provided plus your own confirmed knowledge. If uncertain, mark [REQUIRES VERIFICATION — what to check]. NEVER fabricate holdings, quotes, judges or citations.\n` +
        `2. If you do not actually know this case with confidence, output exactly: "INSUFFICIENT INFORMATION — please paste the judgment or a reliable summary." and nothing else.\n` +
        `3. Every legal proposition in "Reasoning" and "Significance" carries an OSCOLA footnote [^n]. List full OSCOLA footnotes under a final "Footnotes" heading.\n` +
        `4. Post-2000 SA judgments: use [paragraph] pinpoints in square brackets. Distinguish ratio from obiter explicitly.` + overlay,
      );
      const user =
        `Case: ${data.caseName}\nCitation (if known): ${data.citation || "(unknown)"}\n` +
        `User facts/summary: ${data.facts || "(none provided)"}\nUser notes: ${data.notes || "(none)"}`;
      const out = await callGateway(
        [{ role: "system", content: system }, { role: "user", content: user }],
        0.2,
      );
      if (!out || out.toLowerCase().includes("insufficient information")) {
        await refund();
        return {
          ok: false as const,
          error:
            "Couldn't produce a reliable brief — paste the judgment text or a trusted summary and try again.",
        };
      }
      return { ok: true as const, output: out, disclaimer: DISCLAIMER };
    });
    return { ...result, balance };
  });

// ---------------- Source verifier (citation format guardrail) ----------------
// This tool does NOT confirm a source actually exists. It only validates
// citation FORMAT and flags shapes that look suspicious or unverifiable.

const sourcesInput = z.object({
  citations: z.string().trim().min(5).max(8000),
  citationSetId: z.string().uuid().optional(),
});

export const verifySources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => sourcesInput.parse(i))
  .handler(async ({ data, context }) => {
    const { result, balance } = await withCredit(context.userId, "ai_source_verify", async (refund) => {
      const overlay = await loadCitationSetOverlay(context.supabase, context.userId, data.citationSetId);
      const system = withOscola(
        `You are an OSCOLA citation-format checker. You DO NOT confirm whether a source actually exists. ` +
        `For each citation provided by the user, output a JSON array (no prose around it) where each item has: ` +
        `{ "input": string, "style_guess": "OSCOLA"|"Bluebook"|"Harvard"|"APA-7"|"SA-citation"|"unknown", ` +
        `"oscola_compliant": boolean, "oscola_rewrite": string, ` +
        `"verdict": "FORMAT VALID"|"FORMAT QUESTIONABLE"|"UNRECOGNISED FORMAT", ` +
        `"notes": string, "required_verification": string[] }.\n` +
        `Rules:\n` +
        `1. NEVER confirm a case, statute, article or book "exists" — your job is shape, not substance.\n` +
        `2. "oscola_rewrite" MUST re-cast the citation into strict OSCOLA form (italicise case names with *asterisks*, no "v.", square vs round brackets, "s"/"ss"/"art" without full stops, angle-bracketed URLs with access dates). If it cannot be rewritten with confidence, put the original and flag it in notes.\n` +
        `3. "required_verification" lists elements the user MUST verify at the primary source (court, year, section, paragraph/page, reporter volume).\n` +
        `4. Flag impossible volumes, unknown reporters or made-up courts as FORMAT QUESTIONABLE.\n` +
        `5. Split input on newlines or semicolons; each non-empty line is one citation.\n` +
        `6. Output ONLY the JSON array. No markdown fences.` + overlay,
      );
      const out = await callGateway(
        [{ role: "system", content: system }, { role: "user", content: data.citations }],
        0.1,
      );
      if (!out) {
        await refund();
        return { ok: false as const, error: "Could not analyse those citations. Try again." };
      }
      let parsed: unknown = null;
      try {
        const cleaned = out.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "");
        parsed = JSON.parse(cleaned);
      } catch {
        parsed = null;
      }
      if (!Array.isArray(parsed)) {
        return {
          ok: true as const,
          raw: out,
          items: null,
          disclaimer: DISCLAIMER,
        };
      }
      return {
        ok: true as const,
        items: parsed,
        raw: null,
        disclaimer: DISCLAIMER,
      };
    });
    return { ...result, balance };
  });

// ---------------- Judgment summary generator ----------------

const judgmentInput = z.object({
  judgment: z
    .string()
    .trim()
    .min(200, "Paste at least a few paragraphs of the judgment.")
    .max(40000, "Judgment summariser accepts up to ~40,000 characters."),
  caseName: z.string().trim().max(240).optional().default(""),
  citation: z.string().trim().max(240).optional().default(""),
  citationSetId: z.string().uuid().optional(),
});

export const summariseJudgment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => judgmentInput.parse(i))
  .handler(async ({ data, context }) => {
    const { result, balance } = await withCredit(context.userId, "ai_judgment_summary", async (refund) => {
      const overlay = await loadCitationSetOverlay(context.supabase, context.userId, data.citationSetId);
      const system = withOscola(
        `You are a senior South African judgment summariser producing publication-grade summaries. ` +
        `Given a judgment (or extract), produce a structured summary with these headings in this exact order: ` +
        `"Citation" (full OSCOLA form), "Court & coram", "Material facts", "Legal principles engaged", "Ratio decidendi", "Obiter of note", "Order / outcome", "Significance for practice".\n` +
        `Rules:\n` +
        `1. Use ONLY the supplied text. Do NOT add facts, parties, holdings or citations not in the text. Mark gaps as [REQUIRES VERIFICATION].\n` +
        `2. Ratio decidendi is a single tight paragraph — clearly distinguished from obiter.\n` +
        `3. Every principle carries an OSCOLA footnote [^n] pinpointed to the paragraph number in the supplied text. List full OSCOLA footnotes under a final "Footnotes" heading.\n` +
        `4. Quote sparingly with straight quotes and paragraph pinpoints in [square brackets].\n` +
        `5. If the text is too short or non-judicial, output exactly: "INSUFFICIENT TEXT — please paste the full judgment or a longer extract." and nothing else.` + overlay,
      );
      const header = `Case (user-supplied): ${data.caseName || "(unknown)"}\nCitation (user-supplied): ${data.citation || "(unknown)"}\n\n--- JUDGMENT TEXT ---\n`;
      const out = await callGateway(
        [{ role: "system", content: system }, { role: "user", content: header + data.judgment }],
        0.15,
      );
      if (!out || out.toLowerCase().includes("insufficient text")) {
        await refund();
        return {
          ok: false as const,
          error: "Couldn't summarise — paste a longer extract of the judgment and try again.",
        };
      }
      return { ok: true as const, output: out, disclaimer: DISCLAIMER };
    });
    return { ...result, balance };
  });

// ---------------- Assignment assistant ----------------

const assignmentInput = z.object({
  module: z.string().trim().max(200).optional().default(""),
  question: z.string().trim().min(15, "Paste the assignment question.").max(4000),
  wordLimit: z.number().int().min(300).max(20000).optional().default(2000),
  citationStyle: z.string().trim().max(60).optional().default("OSCOLA"),
  jurisdiction: z.string().trim().max(120).optional().default("South Africa"),
  facts: z.string().trim().max(8000).optional().default(""),
  sources: z.string().trim().max(6000).optional().default(""),
  mode: z.enum(["plan", "full_draft", "improve"]).optional().default("plan"),
  existingDraft: z.string().trim().max(30000).optional().default(""),
  citationSetId: z.string().uuid().optional(),
});

export const assignmentAssistant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => assignmentInput.parse(i))
  .handler(async ({ data, context }) => {
    const { result, balance } = await withCredit(context.userId, "ai_assignment", async (refund) => {
      const overlay = await loadCitationSetOverlay(context.supabase, context.userId, data.citationSetId);
      const modeInstruction =
        data.mode === "full_draft"
          ? `Produce a COMPLETE assignment draft to the target word limit that DIRECTLY ANSWERS the assignment question. Use these headings in order: "Introduction", "Legal framework", "Argument 1", "Argument 2", "Counter-argument & response", "Conclusion", "Bibliography", "Footnotes". Introduction ~10% and Conclusion ~10% of the word limit. If the brief has numbered sub-questions (Q1, Q2, 1.1, (a), (b)...), answer EACH one in its own clearly-labelled sub-section under the Body — do not merge them.`
          : data.mode === "improve"
            ? `Take the student's EXISTING DRAFT below and IMPROVE it — sharpen the thesis, tighten reasoning, add missing authority markers, restructure weak paragraphs, and produce a full revised version with the same headings as a full draft. Preserve the student's own arguments where they are sound; do not invent facts. Make sure the revised draft ACTUALLY ANSWERS every part of the assignment question (including every numbered sub-question) — if the original draft missed a sub-question, add a section that answers it. End with a short "Editor's notes" section listing key improvements and any sub-questions that were previously unanswered.`
            : `Produce a detailed ASSIGNMENT PLAN (not a full draft) that shows exactly how the student will answer the question. Use these headings in order: "Question breakdown (issue-by-issue, including every sub-question)", "Thesis / direct answer in one sentence", "Structure (paragraph-by-paragraph outline mapped to each sub-question)", "Key authorities to cite (with pinpoints)", "Counter-argument to address", "Writing tips specific to this question", "Suggested reading list".`;

      const system = withOscola(
        `You are a senior legal-writing tutor for LLB students, candidate attorneys and postgraduate law students. ` +
        `Your FIRST job is to READ THE ASSIGNMENT BRIEF CAREFULLY and DO EXACTLY WHAT IT ASKS — nothing more, nothing less. ` +
        `You help students PLAN and WRITE assignments to a distinction standard without fabricating anything — every argument must be defensible and every authority traceable. ` +
        `${modeInstruction}\n\n` +
        `MANDATORY FIRST STEP — "Brief analysis" (always output this section at the very top, before anything else):\n` +
        `  a) Restate the assignment question in your own words.\n` +
        `  b) List EVERY discrete task the brief requires — every sub-question, every instruction verb (discuss, compare, critically analyse, advise, draft...), every constraint (word limit, jurisdiction, prescribed sources, format).\n` +
        `  c) Confirm which of those tasks this response addresses. If any task is impossible without more information, say so explicitly and ask for it — do not silently skip it.\n\n` +
        `Rules:\n` +
        `1. ANSWER THE QUESTION ACTUALLY ASKED. Do not drift into a general essay on the topic. If the brief says "advise the client", advise the client. If it says "critically discuss", take a position and defend it. If it lists sub-questions (1, 2, 3 / a, b, c / (i), (ii)), answer each one under its own labelled sub-heading and in the SAME ORDER.\n` +
        `2. Follow SA LLB assignment conventions: Introduction (context, precise question, signpost), Body (framework then arguments then counter-argument), Conclusion (direct answer, no new authority), Bibliography (Cases / Legislation / Books / Journals / Online sources).\n` +
        `3. Every legal proposition MUST carry a footnote marker [^n] and a full ${data.citationStyle} footnote listed under a final "Footnotes" heading.\n` +
        `4. If uncertain about any citation, mark it [REQUIRES VERIFICATION — what to check]. NEVER invent case names, section numbers, journal volumes or authors.\n` +
        `5. Stay within ${data.jurisdiction} law. Prefer primary authority (Constitution, statute, binding case) over secondary sources. If the student listed prescribed sources, USE them — cite each at least once where relevant, and note which ones you could not fit and why.\n` +
        `6. Use the given facts. If a scenario is supplied, apply the law to THOSE facts (IRAC / FIRAC) — do not answer in the abstract.\n` +
        `7. Target word count: ~${data.wordLimit} words (excluding footnotes and bibliography). Do not pad and do not fall more than ~10% short.\n` +
        `8. Academic tone: formal, precise, no contractions, no first person unless the brief requires reflection.\n` +
        `9. End with (a) a "Submission checklist" (word count, authority for every proposition, citations consistent, plagiarism declaration, filename convention) AND (b) a "Question-coverage check" that lists each task from the Brief analysis and marks it ✅ answered / ⚠️ partial (with reason) / ❌ not answered (with reason). If any item is ⚠️ or ❌, tell the student exactly what to add.` +
        overlay,
      );

      const user =
        `Module: ${data.module || "(not specified)"}\n` +
        `Citation style: ${data.citationStyle}\n` +
        `Jurisdiction: ${data.jurisdiction}\n` +
        `Word limit: ${data.wordLimit}\n` +
        `Mode: ${data.mode}\n\n` +
        `ASSIGNMENT QUESTION (answer THIS, exactly as written — including every sub-question):\n"""\n${data.question}\n"""\n\n` +
        `Given facts / scenario (apply the law to THESE facts if present):\n${data.facts || "(none supplied)"}\n\n` +
        `Prescribed / preferred sources (use these where relevant; flag any you cannot use and why):\n${data.sources || "(none supplied — you may recommend authorities, marking any uncertain ones [REQUIRES VERIFICATION])"}\n\n` +
        (data.mode === "improve"
          ? `--- EXISTING DRAFT ---\n${data.existingDraft || "(none supplied)"}\n--- END DRAFT ---\n`
          : "") +
        `\nBefore writing the main output, complete the mandatory "Brief analysis" section. After the main output, complete the "Submission checklist" AND the "Question-coverage check".`;

      const out = await callGateway(
        [{ role: "system", content: system }, { role: "user", content: user }],
        data.mode === "plan" ? 0.25 : 0.2,
      );

      if (!out) {
        await refund();
        return { ok: false as const, error: "Could not generate assignment output. Try clearer inputs." };
      }
      return { ok: true as const, output: out, disclaimer: DISCLAIMER };
    });
    return { ...result, balance };
  });

